import React, { useEffect } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import { Box, InputAdornment, TextField } from 'edenred-ui';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { createRules, FormFields } from '@epi-forms/helpers';
import {
  numberBetween,
  number,
  required as requiredValidation
} from '@epi-helpers/formHelpers/validators';
import { IContractPercentageField } from '@epi-models/pages/Beneficiaries';
import { EmployeeTypeModes } from '@epi-constants/beneficiary';

export const ContractPercentageField = ({
  disabled = false,
  isAddMode = false,
  isPartTime = false
}: IContractPercentageField) => {
  const {
    control,
    watch,
    setValue,
    trigger,
    clearErrors,
    formState: { dirtyFields }
  } = useFormContext();
  const { sizeMedium } = useTheme();
  const { t } = useTranslation();
  const betweenNumber = isPartTime
    ? numberBetween(1, 99)
    : numberBetween(1, 100);
  const rules = createRules([requiredValidation, betweenNumber, number]);
  const name = FormFields.EmployeePercentage;
  const employmentType = watch(FormFields.EmployeeType);

  const {
    field: { onChange, value },
    fieldState: { error }
  } = useController({
    name,
    control,
    rules
  });

  useEffect(() => {
    const isDirty = !!dirtyFields[FormFields.EmployeeType];

    if (employmentType === EmployeeTypeModes.FullTime) {
      setValue(name, '100', { shouldValidate: false });
      clearErrors(name);
      return;
    }

    if (
      [EmployeeTypeModes.Onhold, EmployeeTypeModes.Dropped].includes(
        employmentType
      )
    ) {
      return setValue(name, value);
    }

    if (isPartTime || employmentType === EmployeeTypeModes.TemporaryEmployed) {
      if (isDirty) {
        setValue(name, '', { shouldValidate: true });
        trigger(name, { shouldFocus: true });
      } else {
        setValue(name, value);
        clearErrors(name);
      }
      return;
    }

    return setValue(name, '');
  }, [employmentType]);

  return (
    <TextField
      disabled={disabled}
      id={name}
      label={t('containers.beneficiaries.detail.working_hours_as_percentage')}
      value={value}
      onChange={onChange}
      readOnly={false}
      error={!!error}
      helperText={error?.message}
      required
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Box fontSize={sizeMedium}>%</Box>
          </InputAdornment>
        )
      }}
    />
  );
};
